import React, { useState } from 'react';
import confetti from 'canvas-confetti';
import { getTodoStatus, formatDate, getDaysUntil } from '../utils/helpers';
import { isPushSupported, getPushPermission, subscribeToPush } from '../utils/push';

const STATUS_BADGE = {
  overdue: 'bg-red-100 text-red-600',
  'due-soon': 'bg-sunny-100 text-sunny-700',
  upcoming: 'bg-gray-100 text-gray-500',
};

function deadlineLabel(todo) {
  const status = getTodoStatus(todo);
  if (status === 'none' || status === 'done') return null;
  const time = new Date(todo.deadline).toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit' });
  const days = getDaysUntil(todo.deadline);
  let text;
  if (status === 'overdue') text = days === 0 ? `Overdue · ${time}` : `${Math.abs(days)}d overdue`;
  else if (days === 0) text = `Today ${time}`;
  else if (days === 1) text = `Tomorrow ${time}`;
  else text = `${formatDate(todo.deadline)} · ${time}`;
  return { text, className: STATUS_BADGE[status] };
}

function fireTodoConfetti() {
  confetti({
    particleCount: 30,
    spread: 60,
    origin: { x: 0.5, y: 0.7 },
    colors: ['#a855f7', '#14b8a6', '#f97316', '#eab308'],
    ticks: 50,
    gravity: 1.2,
    decay: 0.94,
    startVelocity: 18,
  });
}

export default function TodoList({ todos, members, user, onAdd, onToggle, onDelete }) {
  const [text, setText] = useState('');
  const [assignedTo, setAssignedTo] = useState([]);
  const [deadline, setDeadline] = useState('');
  const [filter, setFilter] = useState('all'); // 'all' | 'mine'
  const [removingId, setRemovingId] = useState(null);
  const [pushPermission, setPushPermission] = useState(getPushPermission());
  const [pushBusy, setPushBusy] = useState(false);

  const myMember = members.find(m => m.email === user?.email);

  const toggleAssignee = (id) => {
    setAssignedTo(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed) return;
    const deadlineIso = deadline ? new Date(deadline).toISOString() : null;
    onAdd(trimmed, assignedTo, deadlineIso);
    setText('');
    setAssignedTo([]);
    setDeadline('');
  };

  const handleToggle = (todo) => {
    if (!todo.done) {
      setRemovingId(todo.id);
      fireTodoConfetti();
      setTimeout(() => {
        onToggle(todo.id);
        setRemovingId(null);
      }, 350);
    } else {
      onToggle(todo.id);
    }
  };

  const handleEnableReminders = async () => {
    setPushBusy(true);
    try {
      const perm = await subscribeToPush();
      setPushPermission(perm);
    } catch (e) {
      console.error('Failed to enable reminders:', e);
      alert(e.message || "Couldn't enable reminders.");
    } finally {
      setPushBusy(false);
    }
  };

  const memberById = (id) => members.find(m => m.id === id);

  const visible = todos.filter(t =>
    filter === 'all' || (myMember && t.assignedTo?.includes(myMember.id))
  );

  const active = visible.filter(t => !t.done).sort((a, b) => {
    if (!a.deadline && !b.deadline) return 0;
    if (!a.deadline) return 1;
    if (!b.deadline) return -1;
    return new Date(a.deadline) - new Date(b.deadline);
  });
  const done = visible.filter(t => t.done);

  const showReminderCta = isPushSupported() && pushPermission !== 'granted';

  return (
    <div className="animate-fade-in-up">
      <div className="mb-6 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">✅ To-Do List</h1>
          <p className="text-sm text-gray-500 mt-1">
            {active.length === 0 ? 'Nothing to do — nice!' : `${active.length} task${active.length !== 1 ? 's' : ''} to do`}
          </p>
        </div>
        {showReminderCta && (
          <button
            onClick={handleEnableReminders}
            disabled={pushBusy}
            className="shrink-0 px-3 py-2 text-sm rounded-xl bg-purple-50 text-purple-600 font-semibold hover:bg-purple-100 transition-colors disabled:opacity-50"
          >
            {pushBusy ? 'Enabling…' : '🔔 Enable reminders'}
          </button>
        )}
      </div>

      {/* Add form */}
      <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-4 shadow-sm border border-purple-100 mb-5 space-y-3">
        <div className="flex gap-2">
          <input
            type="text"
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder="Add a task…"
            className="flex-1 px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-purple-400 focus:outline-none transition-colors text-sm"
          />
          <button
            type="submit"
            className="px-4 py-2.5 bg-gradient-to-r from-purple-500 to-teal-500 hover:from-purple-600 hover:to-teal-600 text-white rounded-xl font-semibold transition-colors text-sm"
          >
            Add
          </button>
        </div>

        {members.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-gray-500 mb-1.5">Assign to</p>
            <div className="flex flex-wrap gap-2">
              {members.map(m => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => toggleAssignee(m.id)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                    assignedTo.includes(m.id) ? 'text-white shadow-md' : 'bg-gray-100 text-gray-500'
                  }`}
                  style={assignedTo.includes(m.id) ? { backgroundColor: m.colour } : {}}
                >
                  {m.name}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold text-gray-500">Deadline</label>
          <input
            type="datetime-local"
            value={deadline}
            onChange={e => setDeadline(e.target.value)}
            className="px-3 py-1.5 border-2 border-gray-200 rounded-xl focus:border-purple-400 focus:outline-none transition-colors text-sm"
          />
          {deadline && (
            <button
              type="button"
              onClick={() => setDeadline('')}
              className="text-xs text-gray-400 hover:text-gray-600"
            >
              Clear
            </button>
          )}
        </div>
      </form>

      {/* Filter */}
      {myMember && (
        <div className="flex gap-2 mb-4">
          {[{ id: 'all', label: 'Everyone' }, { id: 'mine', label: 'Mine' }].map(f => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                filter === f.id ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-500'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      )}

      {/* Active list */}
      {active.length === 0 && done.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-6">No tasks yet — add one above!</p>
      ) : (
        <ul className="space-y-2">
          {active.map(todo => {
            const badge = deadlineLabel(todo);
            return (
              <li
                key={todo.id}
                className={`bg-white rounded-xl p-3 shadow-sm border border-gray-100 flex items-start gap-3 transition-all ${
                  removingId === todo.id ? 'opacity-0 translate-x-4' : ''
                }`}
                style={{ transition: 'opacity 0.35s, transform 0.35s' }}
              >
                <button
                  onClick={() => handleToggle(todo)}
                  className="mt-0.5 w-5 h-5 rounded-md border-2 border-gray-300 hover:border-purple-400 flex-shrink-0 flex items-center justify-center transition-colors"
                  aria-label={`Mark ${todo.text} as done`}
                >
                  {removingId === todo.id && (
                    <svg className="w-3 h-3 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-800">{todo.text}</p>
                  <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                    {badge && (
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${badge.className}`}>
                        {badge.text}
                      </span>
                    )}
                    {(todo.assignedTo || []).map(id => {
                      const m = memberById(id);
                      if (!m) return null;
                      return (
                        <span
                          key={id}
                          className="inline-flex items-center gap-1 text-xs font-medium text-white px-2 py-0.5 rounded-full"
                          style={{ backgroundColor: m.colour }}
                        >
                          {m.name}
                        </span>
                      );
                    })}
                  </div>
                </div>
                <button
                  onClick={() => onDelete(todo.id)}
                  className="p-1.5 rounded-lg hover:bg-red-100 text-gray-300 hover:text-red-500 text-sm transition-colors flex-shrink-0"
                  aria-label="Delete task"
                >
                  🗑️
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {/* Completed */}
      {done.length > 0 && (
        <div className="mt-6">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
            Done ({done.length})
          </p>
          <ul className="space-y-2">
            {done.map(todo => (
              <li key={todo.id} className="bg-gray-50 rounded-xl p-3 flex items-center gap-3">
                <button
                  onClick={() => onToggle(todo.id)}
                  className="w-5 h-5 rounded-md bg-purple-500 flex-shrink-0 flex items-center justify-center"
                  aria-label={`Mark ${todo.text} as not done`}
                >
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </button>
                <span className="text-sm text-gray-400 line-through flex-1 truncate">{todo.text}</span>
                <button
                  onClick={() => onDelete(todo.id)}
                  className="p-1.5 rounded-lg hover:bg-red-100 text-gray-300 hover:text-red-500 text-sm transition-colors flex-shrink-0"
                  aria-label="Delete task"
                >
                  🗑️
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
